import { useEffect, useState, type FormEvent } from 'react';
import { BrowserRouter, Routes, Route, Link, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type BotSummary } from './lib/api';
import { supabase, signOut, type Session } from './lib/supabase';
import { SignIn } from './components/SignIn';

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 3000, refetchInterval: 5000 } },
});

function Header({ session }: { session: Session }) {
  const { data: health } = useQuery({ queryKey: ['health'], queryFn: api.health });
  return (
    <header className="border-b border-zinc-800 bg-zinc-900/40">
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
        <Link to="/" className="text-amber-400 font-semibold">Deadunlock Bot Admin</Link>
        <span className="text-xs text-zinc-500">
          {health
            ? `${health.botsLoggedOn}/${health.botCount} logged on · ${health.botsGcReady} GC ready · ${health.totalFriends} friends`
            : 'connecting…'}
        </span>
        <span className="flex-1" />
        <span className="text-xs text-zinc-400">{session.user.email}</span>
        <button
          onClick={() => signOut().then(() => window.location.reload())}
          className="text-xs text-zinc-400 hover:text-rose-300 cursor-pointer"
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

function BotCard({ bot }: { bot: BotSummary }) {
  const qc = useQueryClient();
  const { state, pendingGuard } = bot;
  const dot = state.gcReady ? 'bg-emerald-400' : state.loggedOn ? 'bg-amber-400' : 'bg-rose-500';
  const status = state.gcReady ? 'GC ready' : state.loggedOn ? 'logged on, GC pending' : 'offline';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function login() {
    setBusy(true); setError(null);
    try {
      const res = await api.bot(bot.id).login();
      if (res.status === 'needs_guard') {
        const code = window.prompt(`Steam Guard code (${res.domain ? `email ${res.domain}` : 'mobile authenticator'}):`);
        if (code) {
          const sub = await api.bot(bot.id).submitGuard(code.trim());
          if (sub.status !== 'submitted') setError('guard submission failed');
        }
      } else if (res.status === 'error') {
        setError(res.error ?? 'login failed');
      }
      qc.invalidateQueries({ queryKey: ['bots'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    setBusy(true);
    try {
      await api.bot(bot.id).logout();
      qc.invalidateQueries({ queryKey: ['bots'] });
    } finally { setBusy(false); }
  }

  async function remove() {
    if (!window.confirm(`Delete bot ${bot.label}? This logs it out and removes it from .bots.json.`)) return;
    setBusy(true);
    try {
      await api.removeBot(bot.id);
      qc.invalidateQueries({ queryKey: ['bots'] });
    } finally { setBusy(false); }
  }

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
      <Link to={`/bots/${bot.id}`} className="block hover:opacity-90">
        <div className="flex items-center gap-2 mb-2">
          <span className={`w-2 h-2 rounded-full ${dot}`} />
          <span className="text-sm font-semibold text-zinc-100">{bot.label}</span>
          <span className="text-xs text-zinc-500 ml-auto font-mono">{bot.id}</span>
        </div>
        <div className="text-xs text-zinc-400 mb-1">{bot.username} · {status}</div>
        <div className="text-xs text-zinc-500 mb-3">
          {bot.friendsCount} / 2000 friends · {bot.callsCount} calls ·
          {' '}<span className={bot.gcRate.callsInLastMinute >= bot.gcRate.max * 0.8 ? 'text-amber-400' : ''}>
            {bot.gcRate.callsInLastMinute}/{bot.gcRate.max} GC/min
          </span>
          {bot.state.errors.length > 0 && <span className="text-rose-400"> · {bot.state.errors.length} errors</span>}
        </div>
      </Link>
      <div className="flex gap-2">
        {state.loggedOn ? (
          <button onClick={logout} disabled={busy}
            className="px-2 py-1 rounded border border-zinc-700 hover:border-amber-500/50 hover:text-amber-300 text-[11px] text-zinc-300 disabled:opacity-50 cursor-pointer">
            {busy ? '…' : 'Log out'}
          </button>
        ) : (
          <button onClick={login} disabled={busy}
            className="px-2 py-1 rounded bg-amber-400 hover:bg-amber-300 text-zinc-950 text-[11px] font-semibold disabled:opacity-50 cursor-pointer">
            {busy ? '…' : pendingGuard.needed ? 'Resume login' : 'Log in'}
          </button>
        )}
        <button onClick={remove} disabled={busy}
          className="ml-auto px-2 py-1 rounded border border-zinc-800 hover:border-rose-500/50 hover:text-rose-300 text-[11px] text-zinc-500 disabled:opacity-50 cursor-pointer">
          Delete
        </button>
      </div>
      {error && <div className="mt-2 text-[11px] text-rose-300">{error}</div>}
    </div>
  );
}

function Overview() {
  const { data, error } = useQuery({ queryKey: ['bots'], queryFn: api.listBots });
  if (error) return <div className="text-rose-300 p-6">{(error as Error).message}</div>;
  const bots = data?.bots ?? [];
  return (
    <div className="max-w-6xl mx-auto px-6 py-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-zinc-100">Bots ({bots.length})</h2>
        <Link to="/add" className="px-3 py-1.5 rounded-lg bg-amber-400 hover:bg-amber-300 text-zinc-950 text-xs font-semibold">
          + Add bot
        </Link>
      </div>
      {bots.length === 0 ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-8 text-center text-sm text-zinc-400">
          No bots in the pool. Click "Add bot" to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {bots.map((b) => <BotCard key={b.id} bot={b} />)}
        </div>
      )}
    </div>
  );
}

function formatDuration(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}
function formatRelative(ts: number): string {
  const d = Math.floor((Date.now() - ts) / 1000);
  if (d < 60) return `${d}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}
function formatStartTime(ts?: number): string {
  return ts ? new Date(ts * 1000).toLocaleString() : '—';
}
function formatMatchDur(s?: number): string {
  if (!s) return '—';
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

interface MatchEntry {
  match_id: string;
  hero_id?: number;
  start_time?: number;
  match_result?: number;
  player_team?: number;
  player_kills?: number;
  player_deaths?: number;
  player_assists?: number;
  match_duration_s?: number;
  net_worth?: number;
}

function MatchRow({ m, botId }: { m: MatchEntry; botId: string }) {
  const [meta, setMeta] = useState<{ loading: boolean; result?: unknown; error?: string }>({ loading: false });
  const won = m.match_result === m.player_team;
  async function fetchMeta() {
    setMeta({ loading: true });
    try {
      const result = await api.bot(botId).metadata(m.match_id);
      setMeta({ loading: false, result });
    } catch (err) {
      setMeta({ loading: false, error: err instanceof Error ? err.message : String(err) });
    }
  }
  return (
    <>
      <tr onClick={fetchMeta} className="border-t border-zinc-800/50 hover:bg-zinc-900/60 cursor-pointer">
        <td className="font-mono text-amber-300 pr-3 py-1">{m.match_id}</td>
        <td className="text-zinc-400 pr-3 py-1">{formatStartTime(m.start_time)}</td>
        <td className="text-zinc-300 pr-3 py-1">#{m.hero_id ?? '?'}</td>
        <td className="font-mono text-zinc-300 pr-3 py-1">
          {m.player_kills ?? 0}/{m.player_deaths ?? 0}/{m.player_assists ?? 0}
        </td>
        <td className="text-zinc-400 pr-3 py-1">{m.net_worth?.toLocaleString() ?? '—'}</td>
        <td className="text-zinc-400 pr-3 py-1">{formatMatchDur(m.match_duration_s)}</td>
        <td className={`pr-3 py-1 font-semibold ${won ? 'text-emerald-300' : 'text-rose-300'}`}>{won ? 'W' : 'L'}</td>
      </tr>
      {(meta.loading || meta.result !== undefined || meta.error) && (
        <tr><td colSpan={7} className="px-2 pb-2">
          {meta.loading && <div className="text-[11px] text-zinc-400">Fetching salts…</div>}
          {meta.error && <div className="text-[11px] text-rose-300">{meta.error}</div>}
          {meta.result !== undefined && (
            <pre className="text-[11px] text-zinc-300 bg-zinc-950 border border-zinc-800 rounded p-2 overflow-x-auto">
              {JSON.stringify(meta.result, null, 2)}
            </pre>
          )}
        </td></tr>
      )}
    </>
  );
}

function FriendRow({ friend, botId }: { friend: { steamId: string; accountId: number; personaName: string | null; friendedAt: number; source: string }; botId: string }) {
  const [matches, setMatches] = useState<{ loading: boolean; data?: MatchEntry[]; friended?: boolean; error?: string } | null>(null);
  async function toggle() {
    if (matches !== null) { setMatches(null); return; }
    setMatches({ loading: true });
    try {
      const res = await api.bot(botId).history(friend.steamId);
      setMatches({ loading: false, data: res.matches as MatchEntry[], friended: res.friended });
    } catch (err) {
      setMatches({ loading: false, error: err instanceof Error ? err.message : String(err) });
    }
  }
  return (
    <div>
      <div className="flex items-center gap-3 px-4 py-2.5">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-zinc-100 font-medium truncate">
            {friend.personaName ?? <span className="text-zinc-500 italic">fetching name…</span>}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono flex gap-3 mt-0.5">
            <span>steam64: {friend.steamId}</span>
            <span>account_id: {friend.accountId}</span>
          </div>
        </div>
        <span className="text-xs text-zinc-500 w-20 text-right shrink-0">{formatRelative(friend.friendedAt)}</span>
        <button onClick={toggle} disabled={matches?.loading}
          className="px-2 py-1 rounded border border-zinc-700 hover:border-amber-500/50 hover:text-amber-300 text-[11px] text-zinc-300 disabled:opacity-50 cursor-pointer shrink-0">
          {matches?.loading ? 'Loading…' : matches !== null ? 'Hide matches' : 'Show matches'}
        </button>
      </div>
      {matches !== null && !matches.loading && matches.error && (
        <div className="px-4 py-2 text-xs text-rose-300 bg-rose-950/20">{matches.error}</div>
      )}
      {matches !== null && !matches.loading && matches.data && matches.data.length === 0 && (
        <div className="px-4 py-2 text-xs text-zinc-400 bg-zinc-950/40">
          No matches{matches.friended === false ? ' (not friended with this bot)' : ''}.
        </div>
      )}
      {matches !== null && matches.data && matches.data.length > 0 && (
        <div className="px-4 py-2 bg-zinc-950/40 overflow-x-auto">
          <div className="text-[11px] text-zinc-500 mb-2">{matches.data.length} matches · click any row to fetch its salts</div>
          <table className="w-full text-[11px]">
            <thead><tr className="text-zinc-500 text-left">
              <th className="font-normal pr-3 pb-1">match_id</th>
              <th className="font-normal pr-3 pb-1">when</th>
              <th className="font-normal pr-3 pb-1">hero</th>
              <th className="font-normal pr-3 pb-1">K/D/A</th>
              <th className="font-normal pr-3 pb-1">net</th>
              <th className="font-normal pr-3 pb-1">dur</th>
              <th className="font-normal pr-3 pb-1">result</th>
            </tr></thead>
            <tbody>{matches.data.map((m) => <MatchRow key={m.match_id} m={m} botId={botId} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BotDetail() {
  const { id } = useParams<{ id: string }>();
  const stateQ = useQuery({ queryKey: ['bot', id, 'state'], queryFn: () => api.bot(id!).state(), enabled: !!id });
  const friendsQ = useQuery({ queryKey: ['bot', id, 'friends'], queryFn: () => api.bot(id!).friends(), enabled: !!id && !!stateQ.data?.bot.loggedOn });
  const callsQ = useQuery({ queryKey: ['bot', id, 'calls'], queryFn: () => api.bot(id!).calls(50), enabled: !!id && !!stateQ.data?.bot.loggedOn });

  if (!id) return null;
  if (!stateQ.data) return <div className="p-6 text-zinc-400">Loading…</div>;
  const { summary, bot, pendingGuard } = stateQ.data;
  const uptimeS = Math.floor((Date.now() - bot.startedAt) / 1000);
  const dot = bot.gcReady ? 'bg-emerald-400' : bot.loggedOn ? 'bg-amber-400' : 'bg-rose-500';
  const status = bot.gcReady ? 'GC ready' : bot.loggedOn ? 'logged on, GC pending' : 'offline';
  const friends = friendsQ.data?.friends ?? [];
  const calls = callsQ.data?.calls ?? [];

  return (
    <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
      <div>
        <Link to="/" className="text-xs text-zinc-400 hover:text-amber-300">← all bots</Link>
        <h2 className="text-xl font-semibold text-zinc-100 mt-1">{summary.label}</h2>
      </div>

      {/* Status card */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${dot}`} />
          <span className="text-sm font-semibold text-zinc-100">{status}</span>
          <span className="text-xs text-zinc-500 ml-auto">up {formatDuration(uptimeS)}</span>
        </div>
        <dl className="grid grid-cols-2 gap-2 text-xs">
          <dt className="text-zinc-500">Bot id</dt>
          <dd className="text-zinc-300 font-mono">{summary.id}</dd>
          <dt className="text-zinc-500">Username</dt>
          <dd className="text-zinc-300 font-mono">{summary.username}</dd>
          <dt className="text-zinc-500">SteamID</dt>
          <dd className="text-zinc-300 font-mono">{bot.steamId ?? '—'}</dd>
          <dt className="text-zinc-500">Friends</dt>
          <dd className="text-zinc-300">{summary.friendsCount} / 2000</dd>
          <dt className="text-zinc-500">Reconnects</dt>
          <dd className="text-zinc-300">{bot.reconnects}</dd>
          <dt className="text-zinc-500">Shared secret</dt>
          <dd className="text-zinc-300">{summary.hasSharedSecret ? 'stored' : 'not stored'}</dd>
        </dl>
        {pendingGuard.needed && (
          <div className="mt-3 rounded border border-amber-700/50 bg-amber-950/20 px-3 py-2 text-xs text-amber-200">
            Steam Guard code needed ({pendingGuard.domain ? `email ${pendingGuard.domain}` : 'mobile authenticator'}). Click "Resume login" on the overview card.
          </div>
        )}
      </div>

      {/* Errors */}
      {bot.errors.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-zinc-300 mb-2">Errors ({bot.errors.length})</h3>
          <div className="rounded-lg border border-rose-900/30 bg-rose-950/10 divide-y divide-zinc-800/50 max-h-48 overflow-y-auto">
            {bot.errors.slice().reverse().map((e, i) => (
              <div key={i} className="px-4 py-2 text-xs">
                <span className="text-zinc-500 mr-3">{formatRelative(e.ts)}</span>
                <span className="text-rose-300">{e.msg}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Friends */}
      <div>
        <h3 className="text-sm font-semibold text-zinc-300 mb-2">Friends ({friends.length})</h3>
        {!bot.loggedOn ? (
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 text-sm text-zinc-400">
            Bot is offline. Log in from the overview card to load friends.
          </div>
        ) : friends.length === 0 ? (
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 text-sm text-zinc-400">
            No friends yet. Add the bot ({bot.steamId}) on Steam to test.
          </div>
        ) : (
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 divide-y divide-zinc-800 max-h-[40rem] overflow-y-auto">
            {friends.map((f) => <FriendRow key={f.steamId} friend={f} botId={id} />)}
          </div>
        )}
      </div>

      {/* Recent GC calls */}
      <div>
        <h3 className="text-sm font-semibold text-zinc-300 mb-2">Recent GC calls</h3>
        {!bot.loggedOn ? (
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 text-sm text-zinc-400">
            Bot is offline.
          </div>
        ) : calls.length === 0 ? (
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 text-sm text-zinc-400">
            No GC calls yet.
          </div>
        ) : (
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 divide-y divide-zinc-800 max-h-96 overflow-y-auto">
            {calls.map((c, i) => (
              <div key={i} className="px-4 py-2 text-xs flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full shrink-0 ${c.ok ? 'bg-emerald-400' : 'bg-rose-500'}`} />
                <span className="text-zinc-500 w-16 shrink-0">{formatRelative(c.ts)}</span>
                <span className="text-zinc-300 w-20 shrink-0 uppercase tracking-wider text-[10px]">{c.kind}</span>
                <span className="text-zinc-300 font-mono w-32 shrink-0 truncate">{c.target}</span>
                <span className="text-zinc-400 flex-1 truncate">{c.ok ? c.resultSummary : c.error}</span>
                <span className="text-zinc-500 w-12 text-right">{c.durationMs}ms</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AddBot() {
  const [form, setForm] = useState({ label: '', username: '', password: '', sharedSecret: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.addBot({
        label: form.label || undefined,
        username: form.username,
        password: form.password,
        sharedSecret: form.sharedSecret || undefined,
      });
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-md mx-auto px-6 py-6">
      <Link to="/" className="text-xs text-zinc-400 hover:text-amber-300">← all bots</Link>
      <h2 className="text-lg font-semibold text-zinc-100 my-4">Add bot</h2>
      {error && <div className="rounded border border-rose-800/50 bg-rose-950/30 px-3 py-2 text-xs text-rose-300 mb-4">{error}</div>}
      <form onSubmit={submit} className="space-y-3">
        {(['label', 'username', 'password', 'sharedSecret'] as const).map((k) => (
          <div key={k}>
            <label className="block text-xs text-zinc-400 mb-1">
              {k}{k === 'label' || k === 'sharedSecret' ? <span className="text-zinc-600"> (optional)</span> : null}
            </label>
            <input
              type={k === 'password' || k === 'sharedSecret' ? 'password' : 'text'}
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              required={k === 'username' || k === 'password'}
              disabled={busy}
              className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-sm text-zinc-100 focus:outline-none focus:border-amber-500"
            />
          </div>
        ))}
        <button
          type="submit"
          disabled={busy}
          className="w-full px-4 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-zinc-950 text-sm font-semibold disabled:opacity-50"
        >
          {busy ? 'Creating…' : 'Create bot (does not log in yet)'}
        </button>
      </form>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<Session | null | 'loading'>('loading');

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === 'SIGNED_IN' && s?.user.email) {
        // Light client-side feedback only — the bot is the actual gate.
        // If the email isn't in ADMIN_EMAILS the bot will return 403 on
        // the first /api/admin/* call and we'll show the error there.
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  if (session === 'loading') {
    return <div className="min-h-full flex items-center justify-center text-sm text-zinc-400">Loading…</div>;
  }
  if (!session) {
    return <SignIn />;
  }
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <Header session={session} />
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/add" element={<AddBot />} />
          <Route path="/bots/:id" element={<BotDetail />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
